package io.github.jpuf.portfolio

import com.varabyte.kobweb.compose.css.ScrollBehavior
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.css.textDecorationLine
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.forms.ButtonStyle
import com.varabyte.kobweb.silk.components.forms.ButtonVars
import com.varabyte.kobweb.silk.components.layout.HorizontalDividerStyle
import com.varabyte.kobweb.silk.components.overlay.TooltipStyle
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.init.registerStyleBase
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.addVariantBase
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.extendedByBase
import com.varabyte.kobweb.silk.style.selectors.link
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import com.varabyte.kobweb.silk.theme.modifyStyleBase
import org.jetbrains.compose.web.css.*

@InitSilk
fun initSiteStyles(ctx: InitSilkContext) {
    // This site does not need scrolling itself, but this is a good demonstration for how you might enable this in your
    // own site. Note that we only enable smooth scrolling unless the user has requested reduced motion, which is
    // considered a best practice.
    ctx.stylesheet.registerStyle("html") {
        cssRule(CSSMediaQuery.MediaFeature("prefers-reduced-motion", StylePropertyValue("no-preference"))) {
            Modifier.scrollBehavior(ScrollBehavior.Smooth)
        }
    }

    ctx.stylesheet.registerStyleBase("body") {
        Modifier
            .fontFamily(
                "-apple-system", "BlinkMacSystemFont", "Segoe UI", "Roboto", "Oxygen", "Ubuntu",
                "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", "sans-serif"
            )
            .fontSize(18.px)
            .lineHeight(1.5)
    }

    ctx.theme.modifyStyleBase(HorizontalDividerStyle) {
        Modifier.fillMaxWidth()
    }
}

val HeroContainerStyle = CssStyle {
    base { Modifier.fillMaxWidth().gap(2.cssRem).margin { top(8.vh) } }
}

val GalleryContainerStyle = CssStyle {
    base { Modifier.fillMaxWidth().gap(1.cssRem).margin { top(8.vh) } }
}

val HeadlineTextStyle = CssStyle {
    base {
        Modifier
            .fontFamily("VG5000")
            .fontSize(2.cssRem)
            .textAlign(TextAlign.Center)
            .lineHeight(1.0)
    }
    Breakpoint.MD {
        Modifier
            .fontSize(3.cssRem)
            .textAlign(TextAlign.End)
    }
}

val HeadlineUnderlinedTextStyle = HeadlineTextStyle.extendedByBase {
    underlineColorModifier(colorMode.toSitePalette().brand.accent)
}

val SubheadlineTextStyle = CssStyle {
    base {
        Modifier
            .fontFamily("VG5000")
            .fontSize(1.5.cssRem)
            .textAlign(TextAlign.Center)
            .color(colorMode.toPalette().color.toRgb().copyf(alpha = 0.8f))
            .lineHeight(1.0)
    }
    Breakpoint.MD {
        Modifier
            .fontSize(2.cssRem)
            .textAlign(TextAlign.End)
    }
}

val PostTitleTextStyle = CssStyle.base {
    Modifier
        .fontFamily("VG5000")
        .fontSize(1.5.cssRem)
        .lineHeight(1.0)
}

fun underlineColorModifier(color: Color) = Modifier.styleModifier {
    textDecorationLine(TextDecorationLine.Underline)
    property("text-decoration-color", color)
    property("text-decoration-thickness", "6px")
}

val BodyTextStyle = CssStyle.base {
    Modifier
        .fontFamily("VG5000")
        .fontSize(1.cssRem)
        .textAlign(TextAlign.Start)
        .color(colorMode.toPalette().color.toRgb())
}

val BodyTextStylePadded = BodyTextStyle.extendedByBase {
    Modifier.fillMaxWidth().padding(topBottom = 3.cssRem)
}

val CircleButtonVariant = ButtonStyle.addVariantBase {
    Modifier.padding(0.px).borderRadius(50.percent)
}

val CorneredButtonVariant = ButtonStyle.addVariantBase {
    Modifier.padding(0.px).borderRadius(8.percent)
}

val UncoloredButtonVariant = ButtonStyle.addVariantBase {
    Modifier.setVariable(ButtonVars.BackgroundDefaultColor, Colors.Transparent)
}

val MaxWidthStyle = CssStyle.base {
    Modifier.fontSize(2.cssRem).fillMaxWidth().padding(bottom = 2.cssRem)
}

val TooltipCentreStyle = TooltipStyle.addVariantBase {
    Modifier.textAlign(TextAlign.Center)
}